import * as React from "react";
import { useTranslation, withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Row, Select, Col, Divider, Input, Dropdown, Tag, Radio, MenuProps, Space } from "antd";
import { Modal } from "Components/General/Modal/GenericModal";
import { Button } from "Components/General/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Text from "antd/lib/typography/Text";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { Loader } from "Components/General/Loader";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import { observer } from "mobx-react";
import {
  CalculatorActionButton,
  labelCalculatorStore,
  LabelsSearchConnections,
  LabelsSearchKeys,
  LabelsSearchSortBy,
} from "./LabelCalculatorStore";
import { breadcrumbsStore } from "../Stores/BreadcrumbsStore";
import { MoveFilesForm } from "../Components/MoveFilesForm";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { investigationsLabelsStore } from "Pages/Project/Investigations/Stores/InvestigationsLabelsStore";
import { InvestSearchQueries } from "Pages/Project/Investigations/Components/InvestSearchQueries";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import classNames from "classnames";

type CalculatorMode = "CALCULATOR" | "SEARCH";

interface LabelCalculatorProps extends WithTranslation, ModalComponentProps {
  files?: (API_FileDataEntity | API_UploadFile)[];
  mode?: CalculatorMode;
  dirId?: number;
  dirName?: string;
  focusInput?: boolean;
}

interface LabelCalculatorState {
  isModalVisible: boolean;
  cursorPosition: number;
  dirs: API_TreeElement;
  searchOptions: Record<string, unknown>;
  loading: boolean;
  focused: boolean;
}

export const LabelCalculator = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<LabelCalculatorProps, LabelCalculatorState> {
      private textInput: React.RefObject<HTMLInputElement>;
      private lastTextInput: React.RefObject<HTMLInputElement>;

      constructor(params: LabelCalculatorProps) {
        super(params);
        this.state = {
          isModalVisible: false,
          cursorPosition: -1,
          dirs: null,
          searchOptions: {},
          loading: false,
          focused: true,
        };
        this.textInput = React.createRef();
        this.lastTextInput = React.createRef();
      }

      componentDidMount() {
        (async () => {
          if (this.props.mode === "SEARCH") {
            await labelCalculatorStore.fetchSearchRelations();
          } else {
            const { id, type } = this.fetchData;
            await labelCalculatorStore.fetch(type, id);
            if (labelCalculatorStore.initialRuleValue) {
              this.parseRule(labelCalculatorStore.initialRuleValue);
            }
          }
        })();
      }

      componentWillUnmount() {
        labelCalculatorStore.clear();
      }

      componentDidUpdate(prevProps: Readonly<LabelCalculatorProps>) {
        if (prevProps.focusInput !== this.props.focusInput) {
          this.props.focusInput && this.handleInputFocus();
        }
      }

      get fetchData(): { id: number; type: "file" | "folder" } {
        if (this.props.files?.length === 1) {
          return {
            id: this.props.files[0].id,
            type: "file",
          };
        } else {
          return {
            id: this.props.dirId,
            type: "folder",
          };
        }
      }

      handleInputFocus = () => {
        this.state.cursorPosition === -1 ? this.textInput?.current?.focus() : this.lastTextInput?.current?.focus();
      };

      fetchDirs = async (id: number) => {
        try {
          this.setState({ loading: true });
          const dirs = await API.getData<API_TreeElement>(
            API_URL.AUTO_ORGANISER.suggestDirectoriesToFile(projectsDataStore.projectId, id)
          );
          if (dirs) {
            this.setState({ dirs });
          }
        } catch (err) {
          throw err;
        } finally {
          this.setState({ loading: false });
        }
      };

      parseRule = (rule: string) => {
        const parts = rule.split(",");
        parts.forEach((part) => {
          const values: API_DataLabel[] = [];
          labelCalculatorStore.labelsValues.forEach(
            ({ labels, bgColour, fgColour }) =>
              labels.length && labels.forEach((i) => values.push({ ...i, fgColor: fgColour, bgColor: bgColour }))
          );
          const typeValue = this.actionButtonsConfig.find(({ value }) => value === part);
          const label = values.find((v) => +v.id === +part);
          if (typeValue) {
            this.handleValuePick(typeValue.name, typeValue.value, "button");
          }
          if (label) {
            this.handleValuePick(label.viewName, label.id, "label", label.bgColor, label.fgColor);
          }
        });
      };

      handleSubmit = async () => {
        const { t, files, dirId } = this.props;
        const { id, type } = this.fetchData;
        const fileIds = files?.map((v) => v.id);
        const isFoldersForLabels = projectsDataStore.labelsSettings;

        try {
          if (fileIds?.length === 1) {
            await labelCalculatorStore.saveForFile(fileIds[0]);
          } else if (fileIds?.length > 1) {
            await labelCalculatorStore.saveMultipleFiles(fileIds);
          } else {
            await labelCalculatorStore.save(dirId);
          }

          notificationSuccess(t("Success"), t("Label was created/edited"));
          documentsTableStore.refresh();

          if (type === "file" && isFoldersForLabels?.folderLabels) {
            await this.fetchDirs(id);
          }

          if (labelCalculatorStore.mode === "FILE" && this.state.dirs) {
            this.setState({ isModalVisible: true });
          } else {
            this.props.hideModal();
          }
        } catch (error) {
          notificationError("Error", "Label was not created/edited");
        }
      };

      handleAbort = () => {
        if (this.props.mode === "SEARCH") {
          this.handleReset();
        } else {
          this.props.hideModal();
        }
      };

      handleReset = async () => {
        labelCalculatorStore.clearResult();
        labelCalculatorStore.clearRule();
        this.handleCursorPosition({ cursorPosition: -1, mode: "start" });
        if (this.props.mode === "SEARCH") {
          await investigationsLabelsStore.fetch();
        }
      };

      handleLabelStyle = (typeId: number) => {
        const stylesByType = labelCalculatorStore.labelsTypes.find(({ id }) => id === typeId);
        if (stylesByType) {
          const { color, backgroundColor } = stylesByType;
          labelCalculatorStore.setStyles({ color, backgroundColor, borderColor: backgroundColor });
        }
      };

      handleLabelListPick = () => {
        const labelsByType = labelCalculatorStore.labelsValues.find(
          ({ id }) => id === labelCalculatorStore.activeLabelTypeId
        );
        if (labelsByType) {
          this.handleLabelStyle(labelCalculatorStore.activeLabelTypeId);
          labelCalculatorStore.setList(labelsByType.labels);
          labelCalculatorStore.setFilteredList(labelsByType.labels);
        }
      };

      handleValuePick = async (
        name: string,
        value: string | number,
        type: "label" | "button",
        bgColor?: string,
        color?: string
      ) => {
        const uid = Date.now();
        const rule = labelCalculatorStore.activeRule.slice();
        const result = labelCalculatorStore.result.slice();
        const newRulePiece = { id: uid, value: `${value}` };
        const idx = this.state.cursorPosition + 1;
        let newRule;

        if (typeof result[idx] === "undefined") {
          newRule = [...rule, newRulePiece];
          labelCalculatorStore.setRule(newRule);
          this.renderResultItem(type, name, uid, bgColor, color);
          this.handleCursorPosition({ cursorPosition: newRule.length - 1, mode: "end" });
        } else {
          rule.splice(idx, 0, newRulePiece);
          labelCalculatorStore.setRule(rule);
          this.renderResultItem(type, name, uid, bgColor, color, idx);
          this.handleCursorPosition({ cursorPosition: rule.length - 1, mode: "end" });
        }
        if (this.props.mode === "SEARCH") {
          await investigationsLabelsStore.fetch();
        }
      };

      handleConfirm = async () => {
        if (this.props.mode === "SEARCH") {
          await investigationsLabelsStore.fetch();
        } else {
          await this.handleSubmit();
        }
      };

      handleCursorPosition = ({
        cursorPosition,
        mode,
      }: {
        cursorPosition: number;
        mode?: "start" | "end" | "middle";
      }) => {
        this.setState({ cursorPosition }, () => {
          if (mode === "start" || this.state.cursorPosition === -1) {
            this.textInput.current.focus();
          }
          if (mode === "end") {
            this.lastTextInput?.current?.focus();
          }
        });
      };

      handleFilterList = () => {
        const { inputValue, setFilteredList, activeLabelList } = labelCalculatorStore;
        if (activeLabelList.length && inputValue) {
          const inputVal = inputValue.toLowerCase();
          let newData = [];
          newData = activeLabelList.filter(({ viewName, synonyms }) => {
            const synonym = synonyms.filter((v) => v.viewName.toLowerCase().indexOf(inputVal) !== -1);
            return viewName.toLowerCase().indexOf(inputVal) !== -1 || synonym.length;
          });
          setFilteredList(newData);
        } else {
          this.handleLabelListPick();
        }
      };

      handleRemoveValue = async (uid: number) => {
        const { result, activeRule, setResult, setRule } = labelCalculatorStore;
        const newResult = result.filter(({ id }) => id !== uid);
        const newRule = activeRule.filter(({ id }) => id !== uid);
        this.handleCursorPosition({ cursorPosition: this.state.cursorPosition - 1, mode: "end" });
        setResult(newResult);
        setRule(newRule);
        if (this.props.mode === "SEARCH") {
          await investigationsLabelsStore.fetch();
        }
      };

      handleInvestSearch = async (key: LabelsSearchKeys, value: LabelsSearchConnections | LabelsSearchSortBy) => {
        labelCalculatorStore.setSearchOptions(key, value);
        if (key && key === "sortBy") {
          await labelCalculatorStore.fetchSearchRelations();
        }
      };

      onFocus = (focused: boolean) => {
        this.setState({ focused });
      };

      renderSelectOptions = (data: API_DataType[]) => {
        return data.map(({ viewName, id }) => (
          <Select.Option key={id} value={id}>
            {viewName}
          </Select.Option>
        ));
      };

      getLabelButtonMenuItems = (data: API_DataLabel[]): MenuProps => {
        return {
          onClick: ({ key }) => {
            this.handleValuePick(data.find((i) => i.id === +key).viewName, key, "label");
          },
          className: "calculator-label-options",
          items: data.map((item) => ({
            key: item.id,
            label: item.viewName,
            style: { backgroundColor: item.bgColor, color: item.fgColor },
          })),
        };
      };

      renderResultItem = (
        type: "label" | "button",
        title: string,
        uid: number,
        backgroundColor?: string,
        color?: string,
        positionIdx?: number
      ) => {
        const result = labelCalculatorStore.result.slice();
        const addStyles = backgroundColor && color ? { backgroundColor, color, borderColor: backgroundColor } : {};
        const element =
          type === "label" ? (
            <Tag
              style={{
                ...labelCalculatorStore.labelsStyles,
                ...addStyles,
                marginRight: 0,
              }}
              className="calculator-result-value"
            >
              {title}
            </Tag>
          ) : (
            <Text className="calculator-result-value text">{title}</Text>
          );
        const newResultPiece = { id: uid, element };

        if (typeof positionIdx !== "undefined") {
          result.splice(positionIdx, 0, newResultPiece);
          labelCalculatorStore.setResult(result);
        } else {
          labelCalculatorStore.setResult([...result, newResultPiece]);
        }
      };

      get actionButtonsConfig(): CalculatorActionButton[] {
        return [
          { type: "AND", name: "AND", value: "&" },
          { type: "OR", name: "OR", value: "|" },
          { type: "LEFT_ARROW", name: "(", value: "(" },
          { type: "RIGHT_ARROW", name: ")", value: ")" },
          // { type: 'JUST', name: 'JUST', value: '' },
          { type: "NOT", name: "NOT", value: "!" },
          // { type: 'INHERIT', name: 'INHERITED', value: '' },
        ];
      }

      render() {
        const { t, files, mode, dirName } = this.props;
        const { cursorPosition, isModalVisible, loading, focused } = this.state;
        const {
          result,
          state,
          activeLabelTypeId,
          filteredLabelList,
          labelsStyles,
          labelsTypes,
          labelsValues,
          setTypeId,
          setInputValue,
        } = labelCalculatorStore;
        const definitionText = () =>
          labelCalculatorStore.mode === "FILE" ? (
            files.map((f) => (
              <Text key={f.id} keyboard className="mr-sm">
                {f.name}
              </Text>
            ))
          ) : (
            <Text keyboard>{breadcrumbsStore.currentPathname + (dirName ? `/${dirName}` : "")}</Text>
          );
        const calculatorClass = classNames("calculator relative", { "calculator-padding": mode !== "SEARCH" });
        const resultClass = classNames("calculator-result relative mr-sm", { "calculator-result-focused": focused });

        if (state === "LOADING" && !labelsTypes.length && !labelsValues.length) {
          return <Loader style={{ minHeight: "400px" }} />;
        }

        return (
          <div className={calculatorClass}>
            <Row align="middle" className="mb-md">
              <Row align="middle" className={resultClass}>
                <div className="display">
                  <input
                    ref={this.textInput}
                    onFocus={() => this.onFocus(true)}
                    onBlur={() => this.onFocus(false)}
                    autoFocus={cursorPosition === -1}
                    className="calculator-result-input"
                    onChange={null}
                    defaultValue=""
                    onClick={() => this.handleCursorPosition({ cursorPosition: -1, mode: "start" })}
                  />
                  {result &&
                    result.map(({ id, element }, idx) => (
                      <div key={`${id}`} className="flex-center">
                        <div className="relative">
                          <div className="calculator-result-value-wrapper">
                            <span onClick={() => this.handleRemoveValue(id)}>
                              <FontAwesomeIcon fixedWidth={true} icon={["far", "times"]} color="#ffffff" />
                            </span>
                          </div>
                          {element}
                        </div>
                        {idx !== result.length - 1 && (
                          <input
                            onFocus={() => this.onFocus(true)}
                            onBlur={() => this.onFocus(false)}
                            autoFocus={cursorPosition === idx}
                            className="calculator-result-input"
                            value=""
                            onClick={() => this.handleCursorPosition({ cursorPosition: idx, mode: "middle" })}
                          />
                        )}
                      </div>
                    ))}
                  {result?.length ? (
                    <input
                      onFocus={() => this.onFocus(true)}
                      onBlur={() => this.onFocus(false)}
                      autoFocus={cursorPosition === result.length - 1}
                      className="calculator-result-input flex1"
                      onChange={null}
                      defaultValue=""
                      ref={this.lastTextInput}
                      onClick={() => this.handleCursorPosition({ cursorPosition: result.length - 1, mode: "end" })}
                    />
                  ) : null}
                </div>
                {result && result.length > 0 && (
                  <div className="clear" onClick={this.handleReset}>
                    <FontAwesomeIcon fixedWidth={true} icon={["far", "times"]} size="xs" color="#ffffff" />
                  </div>
                )}
              </Row>
              {mode === "SEARCH" && <InvestSearchQueries />}
            </Row>
            <Divider />
            <Row className="mb-md">
              <Col flex="1">
                <Row align="middle" className="mb-md">
                  <Space.Compact block>
                    <Select
                      style={{ width: "30%" }}
                      value={activeLabelTypeId}
                      onChange={(id) => {
                        setTypeId(+id);
                        this.handleLabelListPick();
                      }}
                    >
                      {this.renderSelectOptions(labelsTypes)}
                    </Select>
                    <Input
                      style={{ width: "70%" }}
                      disabled={!labelsTypes.length || !labelsValues.length}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        this.handleFilterList();
                      }}
                      placeholder={t("Search in labels")}
                      prefix={<FontAwesomeIcon fixedWidth={true} icon={["fas", "search"]} />}
                    />
                  </Space.Compact>
                </Row>
                <Row className="calculator-label-list">
                  {filteredLabelList?.map(({ viewName, id, hasChild, children }) => {
                    return hasChild ? (
                      <Dropdown.Button
                        menu={this.getLabelButtonMenuItems(children)}
                        icon={<FontAwesomeIcon fixedWidth={true} icon={["far", "share-alt"]} />}
                        key={id}
                        className="calculator-label-button"
                        type="primary"
                        onClick={() => this.handleValuePick(viewName, id, "label")}
                        style={{ ...labelsStyles }}
                      >
                        {t(viewName)}
                      </Dropdown.Button>
                    ) : (
                      <Button
                        key={id}
                        className="calculator-label-button"
                        type="primary"
                        onClick={() => this.handleValuePick(viewName, id, "label")}
                        style={{ ...labelsStyles }}
                      >
                        {t(viewName)}
                      </Button>
                    );
                  })}
                </Row>
              </Col>
              {mode === "SEARCH" ? (
                <>
                  <Divider orientation="vertical" className="calculator-divider" />
                  <Col flex="0 1 300px">
                    <LabelsSearchOptions onChange={(k, v) => this.handleInvestSearch(k, v)} />
                  </Col>
                </>
              ) : this.props.files ? null : (
                <>
                  <Divider orientation="vertical" className="calculator-divider" />
                  <Col flex="0 1 300px">
                    <Row>
                      {this.actionButtonsConfig.map(({ name, type, value }) => (
                        <Button
                          key={type}
                          className="calculator-action-button"
                          type="primary"
                          onClick={() => this.handleValuePick(name, value, "button")}
                        >
                          {t(name)}
                        </Button>
                      ))}
                    </Row>
                  </Col>
                </>
              )}
            </Row>
            <Divider />
            {mode !== "SEARCH" && (
              <Row align="middle" className="mb-md">
                <Text>{t("Definition for")}: </Text>
                {definitionText()}
              </Row>
            )}
            <ButtonsRow justify="end" style={{ marginTop: 40 }}>
              <Button type="primary" onClick={this.handleConfirm} loading={state === "LOADING" || loading}>
                {mode !== "SEARCH" ? t("Save") : t("Search")}
              </Button>
              <Modal
                {...modalProps}
                title={t("Move files")}
                open={isModalVisible}
                destroyOnHidden={true}
                onCancel={() => this.setState({ isModalVisible: false })}
                zIndex={1012}
              >
                <MoveFilesForm
                  files={files as API_FileDataEntity[]}
                  onSubmitSuccess={async (id) => {
                    this.setState({ isModalVisible: false });
                    this.handleAbort();
                    labelCalculatorStore.goToFolder(id);
                  }}
                  initialTreeData={this.state.dirs}
                  onMoveFile={labelCalculatorStore.move}
                  module="LABELS_CALCULATOR"
                  onCancel={() => this.setState({ isModalVisible: false })}
                />
              </Modal>
            </ButtonsRow>
          </div>
        );
      }
    }
  )
);

const LabelsSearchOptions: React.FC<{
  onChange: (key: LabelsSearchKeys, value: LabelsSearchConnections | LabelsSearchSortBy) => void;
}> = ({ onChange }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  return (
    <Row>
      <div>
        <span className="mr-sm">{t("Sort")}:</span>
        <Radio.Group
          defaultValue="name"
          size="small"
          buttonStyle="solid"
          onChange={(e) => onChange("sortBy", e.target.value)}
        >
          <Radio.Button value="name">{t("Alphabetically")}</Radio.Button>
          <Radio.Button value="searchCount">{t("Search frequency")}</Radio.Button>
        </Radio.Group>
      </div>
      <div className="mt-2">
        <span className="mr-sm">{t("Combine")}:</span>
        <Radio.Group
          defaultValue="AND"
          size="small"
          buttonStyle="solid"
          onChange={(e) => onChange("allLabels", e.target.value)}
        >
          <Radio.Button value="AND">{t("AND")}</Radio.Button>
          <Radio.Button value="OR">{t("OR")}</Radio.Button>
        </Radio.Group>
      </div>
    </Row>
  );
};
